import { useState, useEffect, useCallback } from "react";
import { useSearchParams, useParams, Link } from "react-router-dom";
import { API, useAuth } from "@/App";
import axios from "axios";
import { logError } from "@/lib/logger";
import DOMPurify from "dompurify";
import { Calendar, ArrowLeft, Share2 } from "lucide-react";
import { Seo } from "@/components/Seo";
import { SITE_URL } from "@/lib/seo";
import { DraftPreviewBar } from "@/components/DraftPreviewBar";

const BlogPost = () => {
  const { slug } = useParams();
  const [searchParams] = useSearchParams();
  const { token, isAuthenticated } = useAuth();
  const isPreview = searchParams.get("preview") === "1" && isAuthenticated;
  const [post, setPost] = useState(null);
  const [loading, setLoading] = useState(true);

  // Placeholder post content
  const placeholderPost = {
    id: "1",
    title: "The Art of Being Present",
    slug: "art-of-being-present",
    excerpt: "In our hyperconnected world, true presence has become the rarest luxury.",
    content: `
      <p>In our hyperconnected world, where notifications demand our attention and our minds race through endless to-do lists, true presence has become perhaps the rarest luxury of all. It's not something you can buy, yet it's the most precious gift you can give another person.</p>

      <h2>What Does It Mean to Be Present?</h2>
      <p>Being present isn't simply about physical proximity. It's about mental and emotional engagement. It's listening without formulating your response. It's seeing without judgment. It's creating a space where another person feels truly seen and valued.</p>

      <p>I've learned that the most memorable encounters aren't defined by extravagant settings or expensive gifts—though those can be lovely. They're defined by the quality of attention exchanged. When two people meet with genuine curiosity and openness, magic happens.</p>

      <h2>The Lost Art</h2>
      <p>Watch people at a restaurant sometime. How many are truly engaged with their companions versus stealing glances at their phones? We've forgotten how to simply be with another person. We're so afraid of silence that we fill every moment with noise.</p>

      <p>But silence can be beautiful. Comfort in silence is the mark of true connection. It means you don't need constant stimulation to enjoy someone's company. You can simply exist together, present in the moment.</p>

      <h2>How I Practice Presence</h2>
      <p>When I'm with someone, I make a conscious choice to be fully there. My phone stays away. My attention is undivided. I ask questions not to fill space, but because I'm genuinely curious. I listen not just to words, but to what lies beneath them.</p>

      <p>This isn't always easy. Our minds wander. Worries intrude. But with practice, presence becomes a gift you can offer more freely—to others and to yourself.</p>

      <h2>The Reward</h2>
      <p>The reward for presence is connection. Real connection. The kind that lingers in your memory long after the moment has passed. The kind that makes life feel meaningful and rich.</p>

      <p>In a world of distractions, be someone's undivided attention. It's the greatest luxury you can offer.</p>
    `,
    featured_image: "https://images.unsplash.com/photo-1506126613408-eca07ce68773?w=1200",
    published: true,
    created_at: "2025-01-10T12:00:00Z"
  };

  const fetchPost = useCallback(async () => {
    try {
      const response = isPreview
        ? await axios.get(`${API}/admin/blog/by-slug/${slug}`, { headers: { Authorization: `Bearer ${token}` } })
        : await axios.get(`${API}/public/blog/${slug}`);
      setPost(response.data);
    } catch (error) {
      logError("Failed to fetch blog post, using placeholder:", error);
      setPost(placeholderPost);
    } finally {
      setLoading(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slug, isPreview, token]);

  useEffect(() => {
    fetchPost();
  }, [fetchPost]);

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' });
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="spinner" />
      </div>
    );
  }

  if (!post) {
    return (
      <div className="min-h-screen flex items-center justify-center px-6">
        <div className="text-center">
          <h1 className="font-heading text-3xl text-charcoal mb-4">Post Not Found</h1>
          <Link to="/blog" className="btn-secondary">
            Return to Blog
          </Link>
        </div>
      </div>
    );
  }

  const articleSchema = {
    "@context": "https://schema.org", "@type": "BlogPosting", headline: post.title, description: post.excerpt,
    image: post.seo_image || post.featured_image || undefined, datePublished: post.created_at, dateModified: post.updated_at || post.created_at,
    author: { "@type": "Person", name: "Kalani Luana", url: SITE_URL }, mainEntityOfPage: `${SITE_URL}/blog/${post.slug}`,
  };

  return (
    <div data-testid="blog-post-page">
      {isPreview && <DraftPreviewBar post={post} onPublished={fetchPost} />}
      <Seo title={post.seo_title || `${post.title} — Kalani Luana`} description={post.seo_description || post.excerpt} canonical={`${SITE_URL}/blog/${post.slug}`} ogImage={post.seo_image || post.featured_image} type="article" jsonLd={articleSchema} />
      {/* Hero Image */}
      {post.featured_image && (
        <section className="relative h-[50vh] md:h-[60vh]" data-testid="post-hero">
          <img
            src={post.featured_image}
            alt={post.title}
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-charcoal/60 to-transparent" />
        </section>
      )}

      {/* Article Content */}
      <article className="py-16 px-6 md:px-12 lg:px-24" data-testid="post-content">
        <div className="max-w-3xl mx-auto">
          {/* Back Link */}
          <Link 
            to="/blog" 
            className="inline-flex items-center gap-2 text-jade-text hover:text-champagne-gold transition-colors mb-8"
            data-testid="back-to-blog"
          >
            <ArrowLeft className="w-4 h-4" />
            <span className="font-body text-sm">Back to Blog</span>
          </Link>

          {/* Header */}
          <header className="mb-12">
            <div className="flex items-center gap-4 text-taupe mb-6">
              <div className="flex items-center gap-2">
                <Calendar className="w-4 h-4" />
                <span className="font-body text-sm">{formatDate(post.created_at)}</span>
              </div>
            </div>
            <h1 className="font-heading text-4xl md:text-5xl text-charcoal mb-6 leading-tight">
              {post.title}
            </h1>
            {post.excerpt && (
              <p className="font-body text-xl text-stone-grey leading-relaxed">
                {post.excerpt}
              </p>
            )}
          </header>

          {/* Content */}
          <div 
            className="prose prose-lg max-w-none
              prose-headings:font-heading prose-headings:text-charcoal prose-headings:font-normal
              prose-h2:text-2xl prose-h2:mt-12 prose-h2:mb-6
              prose-p:font-body prose-p:text-stone-grey prose-p:leading-relaxed prose-p:mb-6
              prose-a:text-jade-text prose-a:no-underline hover:prose-a:text-champagne-gold
              prose-strong:text-charcoal
              prose-blockquote:border-l-champagne-gold prose-blockquote:italic prose-blockquote:text-stone-grey
            "
            dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(post.content || "") }}
          />

          {/* Signature */}
          <div className="mt-16 pt-8 border-t border-beige text-center">
            <p className="font-accent text-3xl text-champagne-gold">
              With Love, Kalani
            </p>
          </div>

          {/* Share */}
          <div className="mt-12 flex items-center justify-center gap-4">
            <span className="font-body text-sm text-taupe">Share this post</span>
            <button 
              className="w-10 h-10 rounded-full bg-sand flex items-center justify-center hover:bg-jade-deep hover:text-white transition-all"
              data-testid="share-button"
            >
              <Share2 className="w-4 h-4" />
            </button>
          </div>
        </div>
      </article>

      {/* More Posts CTA */}
      <section className="py-16 px-6 md:px-12 lg:px-24 bg-blush-pink/20">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="font-heading text-2xl text-charcoal mb-6">
            Enjoyed this post?
          </h2>
          <Link to="/blog" className="btn-secondary inline-flex items-center gap-2">
            Read More Articles
          </Link>
        </div>
      </section>
    </div>
  );
};

export default BlogPost;
